import moment from 'moment';
import { BE_TO_FE_STATUS_MAP, WORKPACK_BACKEND_STATUS, WORKPACK_DISPLAY_STATUS } from './workpackStatusConstants';

const WORKPACK_EXPIRY_DAYS = 28;
const WORKPACK_DAYS_LEFT_THRESHOLD = 7;

/**
 * Gets the most recent workpack based on dateOfPrintSpec
 * @param {Array} workpacks - Array of workpack objects
 * @returns {Object|undefined} - The latest workpack or undefined
 */
export const getLatestWorkpack = (workpacks) => {
    if (!workpacks || workpacks.length === 0) {
        return undefined;
    }

    return [...workpacks].sort((packA, packB) => {
        const dateDifference = new Date(packB.dateOfPrintSpec) - new Date(packA.dateOfPrintSpec);
        // prioritize by dateOfPrintSpec first
        if (dateDifference !== 0) {
            return dateDifference;
        }

        // edge case - identical dates, prioritise the available one first
        const packAPriority = packA.status === WORKPACK_BACKEND_STATUS.AVAILABLE ? 1 : 0;
        const packBPriority = packB.status === WORKPACK_BACKEND_STATUS.AVAILABLE ? 1 : 0;

        return packBPriority - packAPriority;
    })[0];
};

/**
 * Calculates days remaining until workpack request expires (28 days from dateCreated)
 * @param {string} dateCreated - The workpackRequest dateCreated timestamp
 * @returns {number} - Number of days remaining (can be negative if expired)
 */
export const getDaysUntilExpiry = (dateCreated) => {
    if (!dateCreated) {
        return -1;
    }

    const createdDate = moment(dateCreated);
    const expiryDate = createdDate.clone().add(WORKPACK_EXPIRY_DAYS, 'days');
    const now = moment();
    // Use Math.ceil to round up partial days
    return Math.ceil(expiryDate.diff(now, 'days', true));
};

/**
 * Determines if a workpack request is expired based on 28-day rule from dateCreated
 * @param {string} dateCreated - The workpackRequest dateCreated timestamp
 * @returns {boolean} - True if expired (older than 28 days)
 */
export const isWorkpackExpired = (dateCreated) => {
    return getDaysUntilExpiry(dateCreated) < 0;
};

/**
 * Gets the status information for a workpack request based on workpacks and dateCreated
 * @param {Array} workpacks - Array of workpack objects with status field
 * @param {string} dateCreated - The workpackRequest dateCreated timestamp
 * @returns {Object} - Object containing isAvailable, isExpired, and daysRemaining
 */
export const getWorkpackStatus = (workpacks, dateCreated) => {
    const latestWorkpack = getLatestWorkpack(workpacks);
    const backendStatus = latestWorkpack?.status || null;
    const createdTimestamp = latestWorkpack?.dateOfPrintSpec || dateCreated;

    if (!createdTimestamp) {
        return {
            isAvailable: false,
            isExpired: true,
            displayStatus: WORKPACK_DISPLAY_STATUS.EXPIRED,
            daysRemaining: 0
        };
    }

    // Calculate days remaining from creation timestamp.
    const daysRemaining = getDaysUntilExpiry(createdTimestamp);
    const isDateExpired = daysRemaining < 0;
    const isCreatedToday = moment(createdTimestamp).isSame(moment(), 'day');
    const isWithinDaysLeftWindow = daysRemaining <= WORKPACK_DAYS_LEFT_THRESHOLD;

    // no workpack has been generated for this request yet (e.g. backend read model hasn't caught
    // up with the new request) - treat as still creating rather than failed, unless the request
    // itself is past the 28-day window, meaning it never got a workpack in time
    const noWorkpackYetStatus = isDateExpired ? WORKPACK_DISPLAY_STATUS.FAILED : WORKPACK_DISPLAY_STATUS.CREATING;
    let displayStatus = BE_TO_FE_STATUS_MAP[backendStatus] || noWorkpackYetStatus;
    let isAvailable = false;
    let isExpired = displayStatus === WORKPACK_DISPLAY_STATUS.EXPIRED;

    if (backendStatus === WORKPACK_BACKEND_STATUS.AVAILABLE) {
        if (isDateExpired) {
            displayStatus = WORKPACK_DISPLAY_STATUS.EXPIRED;
            isAvailable = false;
            isExpired = true;
        } else if (isCreatedToday) {
            displayStatus = WORKPACK_DISPLAY_STATUS.NEW_TODAY;
            isAvailable = true;
            isExpired = false;
        } else if (isWithinDaysLeftWindow) {
            displayStatus = WORKPACK_DISPLAY_STATUS.AVAILABLE_DAYS_LEFT;
            isAvailable = true;
            isExpired = false;
        } else {
            displayStatus = WORKPACK_DISPLAY_STATUS.AVAILABLE;
            isAvailable = true;
            isExpired = false;
        }
    } else if (displayStatus === WORKPACK_DISPLAY_STATUS.CREATING) {
        isAvailable = false;
        isExpired = false;
    } else if (displayStatus === WORKPACK_DISPLAY_STATUS.FAILED) {
        isAvailable = false;
        isExpired = true;
    }

    return {
        isAvailable,
        isExpired,
        displayStatus,
        daysRemaining: isDateExpired ? 0 : daysRemaining
    };
};
